import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { X } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { renderHelpMarkdown } from './markdown';

interface Props {
  markdown: string;
  /** A help: link was followed. */
  onOpenTopic: (id: string) => void;
  /** An in-app link was followed (the panel closes itself on a phone, where it covers the page). */
  onNavigate?: () => void;
}

/**
 * A topic's text in the panel. Links inside it behave like the rest of the app:
 * help: links open that topic here, /paths navigate without a reload, and an
 * image opens larger on click (or Enter).
 */
export default function HelpBody({ markdown, onOpenTopic, onNavigate }: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const zoomLabel = t('help.zoom');
  const html = useMemo(() => renderHelpMarkdown(markdown, zoomLabel), [markdown, zoomLabel]);
  const [zoom, setZoom] = useState<{ src: string; alt: string; from: HTMLElement } | null>(null);

  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.target as HTMLElement;
    const zoomButton = el.closest<HTMLElement>('[data-help-zoom]');
    if (zoomButton) {
      e.preventDefault();
      const img = zoomButton.querySelector('img');
      setZoom({ src: zoomButton.getAttribute('data-help-zoom') || '', alt: img?.getAttribute('alt') || '', from: zoomButton });
      return;
    }
    const link = el.closest<HTMLAnchorElement>('a');
    if (!link) return;
    const topic = link.getAttribute('data-help-topic');
    if (topic) {
      e.preventDefault();
      onOpenTopic(topic);
      return;
    }
    // Ctrl/Cmd/Shift-click still opens a new tab, as any link would.
    if (link.hasAttribute('data-help-internal') && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      navigate(link.getAttribute('href') || '/');
      onNavigate?.();
    }
  }, [navigate, onNavigate, onOpenTopic]);

  return (
    <>
      {/* The HTML is sanitised in renderHelpMarkdown (help-only allowlist, own images only). */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="khelp-body" onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />
      {zoom && <ImageZoom src={zoom.src} alt={zoom.alt} onClose={() => { const from = zoom.from; setZoom(null); from.focus(); }} />}
    </>
  );
}

function ImageZoom({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // The panel closes on Escape too; this one is the image's.
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      } else if (e.key === 'Tab') {
        // One control in here: keep focus on it.
        e.preventDefault();
        closeRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <div className="khelp-lightbox" role="dialog" aria-modal="true" aria-label={alt || t('help.zoomTitle')} onClick={onClose}>
      <img className="khelp-lightbox__img" src={src} alt={alt} onClick={(e) => e.stopPropagation()} />
      <button ref={closeRef} type="button" className="khelp-lightbox__close" onClick={onClose} aria-label={t('help.zoomClose')}>
        <X size={20} aria-hidden="true" />
      </button>
    </div>,
    document.body,
  );
}
